import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { isDefined } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { Observable } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { TaskWorkspaceStore } from '../../../../../../../core/h3ml/state/task-workspace-store';
import { ObjectClassService } from '../../../../../../../core/shared/misc-services/object-class.service';
import { ShapeService } from '../../../../../../../core/shared/misc-services/shape.service';
import { assertStore } from '../../../../../../../cross-team-shared/misc-utils';
import { ShapeType } from '../../../../../../../team-apis/otterdam/answers/shape-typings';

@Component({
  selector: 'h3-annotation-label',
  templateUrl: './annotation-label.component.html',
  styleUrls: ['./annotation-label.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AnnotationLabelComponent
  extends DirectiveSuperclass
  implements OnInit
{
  @Input({ required: true }) annotationId!: number;
  @Input({ required: true }) annotationType!: 'shape' | 'object';
  label$!: Observable<string>;
  pathLabel$!: Observable<string>;

  private objectClassService = inject(ObjectClassService);
  private shapeService = inject(ShapeService);
  private taskWorkspaceStore = inject(TaskWorkspaceStore);

  ngOnInit() {
    if (this.annotationType === 'shape') {
      this.buildShapeLabel();
    } else {
      this.buildObjectLabel();
      this.buildObjectPathLabel();
    }
  }

  private buildObjectLabel() {
    this.label$ = this.getObjectStore$().pipe(
      switchMap((objectStore) => objectStore('class').$),
      map(
        (className) =>
          this.objectClassService.getClassLabel(className) ??
          'Unspecified class',
      ),
    );
  }

  private buildObjectPathLabel() {
    this.pathLabel$ = this.getObjectStore$().pipe(
      switchMap((objectStore) => objectStore('class').$),
      map(
        (className) =>
          this.objectClassService.getClassPathLabel(className) ?? [],
      ),
      map((pathLabels) => pathLabels.join(' / ')),
    );
  }

  private buildShapeLabel() {
    const shapeStore = this.shapeService.getShapeStore(this.annotationId);
    this.label$ = shapeStore('type').$.pipe(
      // when deleting this is temporally undefined
      filter(isDefined),
      map(getHumanReadableShapeType),
    );
  }

  private getObjectStore$() {
    return this.getInput$('annotationId').pipe(
      map((annotationId: number) => {
        const objectStore = this.taskWorkspaceStore('objects')(annotationId);
        assertStore(objectStore, 'object must exist');
        return objectStore;
      }),
    );
  }
}

function getHumanReadableShapeType(shapeType: ShapeType) {
  return titleCase(shapeType.replace(/_/g, ' '));
}

function titleCase(text: string) {
  return text[0].toUpperCase() + text.slice(1).toLowerCase();
}
